/**
 * ResultsStep - Step 5 of SMB Vendor Cost Import
 *
 * Process files and show results with download links
 * Allow chaining another vendor file or starting over
 */

import React, { useState } from 'react'
import { Space, Card, Alert, Button, Statistic, Row, Col, Typography, Spin, App } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  ReloadOutlined,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { smbSyncService } from '../../../services/smbSyncService'
import { errorService } from '../../../services/errorService'
import type { StepProps, ProcessRequest, CatalogMappings, VendorMappings } from './types'

const { Title, Text } = Typography

type ProcessStatus = 'idle' | 'processing' | 'success' | 'error'

/**
 * Step 5: Process files and show results
 */
export const ResultsStep: React.FC<StepProps> = ({ state, onNext, onBack, onCancel }) => {
  const { modal } = App.useApp()
  const [processStatus, setProcessStatus] = useState<ProcessStatus>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [showExceptionWarning, setShowExceptionWarning] = useState(false)

  const handleProcess = async () => {
    setProcessStatus('processing')

    try {
      // Build catalog mappings from field mapping state
      // New format uses fieldType and selectedColumns (array)
      const catalogMappings: CatalogMappings = {
        'Unique ID': [],
        Price: [],
        Cost: [],
      }

      state.catalogMappings.forEach((m) => {
        if (m.selectedColumns.length > 0) {
          if (m.fieldType === 'Unique ID') catalogMappings['Unique ID'] = m.selectedColumns
          else if (m.fieldType === 'SKU') catalogMappings.SKU = m.selectedColumns
          else if (m.fieldType === 'Vendor Code') catalogMappings['Vendor Code'] = m.selectedColumns
          else if (m.fieldType === 'GTIN') catalogMappings.GTIN = m.selectedColumns
          else if (m.fieldType === 'Price') catalogMappings.Price = m.selectedColumns
          else if (m.fieldType === 'Cost') catalogMappings.Cost = m.selectedColumns
        }
      })

      // Use consolidated vendor mappings from FieldMappingStep
      // state.vendorMappings is already in VendorMappings format (object with string[] values)
      const vendorMappings = state.vendorMappings as unknown as VendorMappings

      // Build pricing rule using generic field type as basis
      const pricingRule = {
        pricing_basis: state.pricingBasis,
        price_type: state.priceCalculationMethod === 'formula' ? ('formula' as const) : ('field' as const),
        ...(state.priceCalculationMethod === 'formula'
          ? {
              multiplier: state.costMultiplier,
              adder: state.costAdder,
              rounding_rule: state.roundingRule,
            }
          : {
              price_field: state.pricingBasis,
            }),
      }

      const request: ProcessRequest = {
        job_id: state.jobId,
        catalog_mappings: catalogMappings,
        vendor_mappings: vendorMappings,
        pricing_rule: pricingRule,
      }

      const response = await smbSyncService.process(request)

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to process files')
      }

      setProcessStatus('success')
      onNext({ processResult: response.data })
    } catch (err: any) {
      const errorMsg = err.message || 'Processing failed'
      setProcessStatus('error')
      setErrorMessage(errorMsg)
      errorService.showError('Processing Failed', errorMsg)
    }
  }

  const handleDownload = async (url: string, filename: string) => {
    try {
      await smbSyncService.downloadResultFile(url, filename)
    } catch (err: any) {
      errorService.showError('Download Failed', 'Failed to download file')
    }
  }

  const handleStartOver = () => {
    modal.confirm({
      title: 'Start a new import?',
      icon: <ReloadOutlined />,
      content: "This will reset the wizard. Make sure you've downloaded any files you need first.",
      okText: 'Start New Import',
      cancelText: 'Stay Here',
      onOk: () => {
        // Reset to beginning
        onNext({
          currentStep: 'catalog',
          jobId: '',
          catalogFile: null,
          vendorFile: null,
          catalogColumns: [],
          vendorSampleData: null,
          tableFieldMappings: [],
          catalogMappings: [],
          vendorMappings: [],
          processResult: null,
        })
      },
    })
  }

  const handleChain = () => {
    // If there are exceptions, show warning modal first
    if (result && result.stats.exceptions > 0) {
      setShowExceptionWarning(true)
      return
    }
    proceedWithChain()
  }

  const proceedWithChain = () => {
    setShowExceptionWarning(false)
    // Go back to vendor upload step but keep catalog and mappings
    // Clear vendor-specific state to avoid stale data from previous vendor file
    onNext({
      currentStep: 'vendor',
      vendorFile: null,
      vendorSampleData: null,
      tableFieldMappings: [],
      vendorTabs: [],
      selectedTabs: [],
      processResult: null,
    })
  }

  const result = state.processResult

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Instructions */}
      <Card>
        <Title level={4}>Step 5: Process & Download Results</Title>
        <Text>
          {processStatus === 'idle' &&
            'Click "Process Files" to apply your mappings and pricing rules.'}
          {processStatus === 'processing' && 'Processing your files...'}
          {processStatus === 'success' && 'Processing complete! Download your results below.'}
          {processStatus === 'error' && 'Processing failed. Please try again or go back to fix mappings.'}
        </Text>
      </Card>

      {/* Processing Button */}
      {processStatus === 'idle' && (
        <Card>
          <Button
            type="primary"
            size="large"
            icon={<CheckCircleOutlined />}
            onClick={handleProcess}
            block
          >
            Process Files
          </Button>
        </Card>
      )}

      {/* Processing State */}
      {processStatus === 'processing' && (
        <Card style={{ textAlign: 'center', padding: '24px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text strong>Processing files...</Text>
            <br />
            <Text type="secondary">This may take a moment</Text>
          </div>
        </Card>
      )}

      {/* Error State */}
      {processStatus === 'error' && (
        <Alert
          message="Processing Failed"
          description={errorMessage}
          type="error"
          showIcon
          icon={<CloseCircleOutlined />}
          action={
            <Button size="small" onClick={handleProcess}>
              Try Again
            </Button>
          }
        />
      )}

      {/* Success State */}
      {processStatus === 'success' && result && (
        <>
          {/* Summary Statistics */}
          <Card>
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Total Catalog"
                  value={result.stats.total_catalog_rows}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Matched"
                  value={result.stats.matched}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Unchanged"
                  value={result.stats.unchanged}
                  valueStyle={{ color: '#8c8c8c' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Exceptions"
                  value={result.stats.exceptions}
                  valueStyle={{ color: result.stats.exceptions > 0 ? '#ff4d4f' : '#52c41a' }}
                  prefix={result.stats.exceptions > 0 ? <WarningOutlined /> : undefined}
                />
              </Col>
            </Row>
          </Card>

          {/* Download Buttons */}
          <Card title="Download Results">
            <Space direction="vertical" style={{ width: '100%' }}>
              {result.output_urls.updated && (
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    handleDownload(result.output_urls.updated!, 'updated-products.csv')
                  }
                  block
                >
                  Download Updated Products ({result.stats.matched})
                </Button>
              )}
              {result.output_urls.all_updated && (
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    handleDownload(result.output_urls.all_updated!, 'all-updated-products.csv')
                  }
                  block
                  style={{ backgroundColor: '#13c2c2', borderColor: '#13c2c2' }}
                >
                  Download All Updated Products (cumulative)
                </Button>
              )}
              {result.output_urls.unchanged && (
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    handleDownload(result.output_urls.unchanged!, 'unchanged-products.csv')
                  }
                  block
                >
                  Download Unchanged Products ({result.stats.unchanged})
                </Button>
              )}
              {result.output_urls.consolidated && (
                <Button
                  type="default"
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    handleDownload(result.output_urls.consolidated!, 'consolidated-catalog.csv')
                  }
                  block
                >
                  Download Consolidated Catalog
                </Button>
              )}
              {result.stats.exceptions > 0 && result.output_urls.exceptions && (
                <Button
                  danger
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    handleDownload(result.output_urls.exceptions!, 'exception-report.csv')
                  }
                  block
                >
                  Download Exception Report ({result.stats.exceptions})
                </Button>
              )}
            </Space>
          </Card>

          {/* Action Buttons */}
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="default"
                icon={<ReloadOutlined />}
                onClick={handleStartOver}
                block
                size="large"
              >
                Start Over
              </Button>
              {/* Exception Warning Alert - Shows when attempting to chain with exceptions */}
              {showExceptionWarning && result.stats.exceptions > 0 && (
                <Alert
                  message="Exception Warning"
                  description={
                    <>
                      There is currently an issue generating some prices that are captured in the
                      Exception Report. If you continue, any exceptions will not be captured in the
                      final upload file and will need to be added back manually.
                    </>
                  }
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  action={
                    <Space direction="vertical" style={{ marginLeft: 16 }}>
                      <Button size="small" type="primary" onClick={proceedWithChain}>
                        Continue Anyway
                      </Button>
                      <Button size="small" onClick={() => setShowExceptionWarning(false)}>
                        Cancel
                      </Button>
                    </Space>
                  }
                />
              )}
              {!showExceptionWarning && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleChain}
                  disabled={!result.can_chain}
                  block
                  size="large"
                >
                  Perform Another Vendor Cost Update
                </Button>
              )}
            </Space>
          </Card>
        </>
      )}

      {/* Back/Cancel buttons (only show before processing) */}
      {processStatus === 'idle' && (
        <Card>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button size="large" onClick={onBack}>
              Back
            </Button>
            <Button size="large" onClick={onCancel}>
              Cancel
            </Button>
          </Space>
        </Card>
      )}

    </Space>
  )
}
